import {
	AddCircle as AddCircleIcon,
	Add as AddIcon,
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	CameraAlt as CameraAltIcon,
	Delete as DeleteIcon,
	RotateLeft as RotateLeftIcon,
	Settings as SettingsIcon,
	Videocam as VideocamIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	AlertProps,
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid,
	Menu,
	MenuItem,
	Select,
	Slider,
	Switch,
	TextField,
	Tooltip,
	Typography,
	useTheme
} from "@mui/material";
import { renderTimeViewClock, TimePicker } from "@mui/x-date-pickers-pro";
import moment, { Moment } from "moment";
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ReactComponent as NoImageIcon } from "../../../assets/icons/image-not-found-icon.svg";
import { AGAccordion, AGAccordionDetails, AGAccordionSummary } from "../../../common/AGAccordion";
import CameraSetupDialog from "../../../common/CameraSetup";
import ConfirmationDialog from "../../../common/ConfirmationDialog";
import NumberInput from "../../../common/NumberInput";
import {
	CameraControlKeys,
	ICameraControlDetails,
	ICameraControlsData,
	ICameraControlValues,
	ICameraDetails,
	ICameraIntegerControlDetails,
	IUpdateCameraProfileDetails
} from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import {
	useDeleteCameraMutation,
	useDeleteCameraProfileMutation,
	useGetCameraProfilesQuery,
	useResetCameraProfileMutation,
	useUpdateCameraProfilesMutation
} from "../../../redux/reducers/configuration.reducer";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import { useGetCamerasListQuery, useGetSiteConfigQuery } from "../../../redux/reducers/settings.reducer";
import { convertSnakeToTitleCase, getRestrictedAccessList, RESTRICTED_ACCESS_E } from "../../../utils/commonUtils";
import { RPI_DEVICE_NAME } from "../../../utils/constants";
import MVPCameraStream from "../../LiveView/MVPCameraStream";
import Stream from "../../LiveView/Stream";
import UNVCameraStream from "../../LiveView/UNVCameraStream";
import "../configuration.css";
import AddProfileDialog from "./AddProfileDialog";
import { maxBy, orderBy } from "lodash";

type Replace<T, From, To> = T extends From ? To : T;

interface ICameraIntegerControlDetailsInput extends Omit<ICameraIntegerControlDetails, "value"> {
	value: string;
}

type ICameraControlValuesInput = Replace<
	ICameraControlValues,
	ICameraIntegerControlDetails,
	ICameraIntegerControlDetailsInput
>;

type ICameraControlDetailsInput = {
	[key in CameraControlKeys]: ICameraControlValuesInput;
};

interface ICameraControlsDataInput extends Omit<ICameraControlsData, "settings"> {
	settings: ICameraControlDetailsInput;
}

const CameraSettings: FC = () => {
	const dispatch = useAppDispatch();
	const theme = useTheme();

	const authState = useAppSelector((state) => state.auth);

	const cameraSelectionDropdownButtonRef = useRef<HTMLButtonElement>(null);

	const [showCameraDetailsDialog, setShowCameraDetailsDialog] = useState<boolean>(false);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
	const [deleteCameraId, setDeleteCameraId] = useState<number>(-1);
	const [showAddProfileDialog, setShowAddProfileDialog] = useState<boolean>(false);

	const [cameraSettings, setCameraSettings] = useState<ICameraControlsDataInput[]>([]);
	const [openAccordion, setOpenAccordion] = useState<number>(-1);
	const [selectedCameraDetails, setSelectedCameraDetails] = useState<ICameraDetails | null>(null);
	const [cameraSelectionDropdownOpen, setCameraSelectionDropdownOpen] = useState<boolean>(false);
	const [addingNewCamera, setAddingNewCamera] = useState<boolean>(false);
	const [deleteProfileId, setDeleteProfileId] = useState<number>(-1);

	// Debounce timeout ref for text inputs
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// APIS
	const { data: deviceConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const defaultEconPath = deviceConfig?.default_econ_path ?? "";

	// GET CAMERAS LIST
	const {
		data: camerasListResponse,
		isLoading: getCamerasListLoading,
		isFetching: getCamerasListFetching,
		refetch: refetchCamerasList
	} = useGetCamerasListQuery({}, { skip: !authState.user.token || authState.user.token === "invalid" });

	// DELETE CAMERA
	const [deleteCamera, { isLoading: deleteCameraLoading, reset: resetDeleteCameraState }] = useDeleteCameraMutation();

	// GET CAMERA PROFILES
	const {
		data: getCameraProfilesResponse,
		isLoading: getCameraProfilesLoading,
		isFetching: getCameraProfilesFetching,
		refetch: refetchCameraProfiles
	} = useGetCameraProfilesQuery(selectedCameraDetails?.id ?? 0, {
		refetchOnMountOrArgChange: true,
		skip:
			!selectedCameraDetails ||
			!selectedCameraDetails.rtsp_uri ||
			!selectedCameraDetails.rtsp_uri.startsWith(defaultEconPath)
	});

	// UPDATE CAMERA PROFILES
	const [updateCameraProfiles, { isLoading: updateCameraProfilesLoading, reset: resetUpdateCameraProfilesState }] =
		useUpdateCameraProfilesMutation();

	// DELETE CAMERA PROFILE
	const [deleteCameraProfile, { isLoading: deleteCameraProfileLoading, reset: resetDeleteCameraProfileState }] =
		useDeleteCameraProfileMutation();

	// RESET CAMERA PROFILE
	const [resetCameraProfile, { isLoading: resetCameraProfileLoading, reset: resetResetCameraProfileState }] =
		useResetCameraProfileMutation();

	const camerasList = useMemo(
		() => orderBy(camerasListResponse?.results ?? [], "site_name", "asc"),
		[camerasListResponse]
	);

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(deviceConfig?.cloud_sync_enabled ?? true),
		[deviceConfig]
	);

	const activeProfile = useMemo<ICameraControlsData | null>(() => {
		if (!getCameraProfilesResponse || getCameraProfilesResponse.length <= 0) return null;
		const filtered = getCameraProfilesResponse.filter((item) => moment(item.start_time, "HH:mm:ss").isBefore(moment()));
		const max = maxBy(filtered, (item) => moment(item.start_time, "HH:mm:ss").valueOf());
		return max ?? getCameraProfilesResponse[getCameraProfilesResponse.length - 1] ?? null;
	}, [getCameraProfilesResponse]);

	function notify(message: string, severity: AlertProps["severity"]) {
		dispatch(showFeedbackNotification({ message, severity }));
	}

	function openCameraDetailsDialog(newCamera: boolean = false) {
		setAddingNewCamera(newCamera);
		setShowCameraDetailsDialog(true);
	}

	function closeCameraDetailsDialog() {
		setShowCameraDetailsDialog(false);
		refetchCamerasList();
	}

	function openDeleteConfirmationDialog() {
		if (selectedCameraDetails) {
			setShowDeleteConfirmationDialog(true);
			setDeleteCameraId(selectedCameraDetails.id);
		}
	}

	function closeDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
		setDeleteCameraId(-1);
	}

	function handleDeleteCamera() {
		if (deleteCameraId < 0) return;

		deleteCamera(deleteCameraId)
			.unwrap()
			.then(() => {
				resetDeleteCameraState();
				refetchCamerasList();
				closeDeleteConfirmationDialog();
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to reset camera settings", "error");
			});
	}

	function changeOpenAccordion(value: number) {
		setOpenAccordion((currentValue) => {
			if (currentValue === value) return -1;
			return value;
		});
	}

	function handleGroupNameChange(index: number, value: string, committed: boolean) {
		setCameraSettings((currentSettings) => {
			const updatedSettings = [...currentSettings];

			if (index >= 0) {
				updatedSettings[index] = {
					...updatedSettings[index],
					name: value,
					updated: committed
				};
			}

			return updatedSettings;
		});
	}

	function handleChangeCameraSettingInput(
		index: number,
		key: CameraControlKeys,
		value: boolean | number | string,
		committed?: boolean
	) {
		setCameraSettings((currentSettings) => {
			const updatedSettings = [...currentSettings];

			if (index >= 0) {
				updatedSettings[index] = {
					...updatedSettings[index],
					updated: committed,
					settings: {
						...updatedSettings[index].settings,
						[key]: {
							...updatedSettings[index].settings[key],
							value: value
						}
					}
				};
			}

			return updatedSettings;
		});
	}

	function handleDebouncedCameraSettingInput(index: number, key: CameraControlKeys, value: number | string) {
		if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

		debounceTimeoutRef.current = setTimeout(() => {
			handleChangeCameraSettingInput(index, key, value, true);
		}, 500); // 500ms delay
	}

	function handleDebouncedGroupNameInput(index: number, value: string) {
		if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

		debounceTimeoutRef.current = setTimeout(() => {
			handleGroupNameChange(index, value, true);
		}, 500);
	}

	function handleChangeTime(index: number, value: Moment | null) {
		setCameraSettings((currentSettings) => {
			const updatedSettings = [...currentSettings];

			if (index >= 0) {
				updatedSettings[index] = {
					...updatedSettings[index],
					start_time: value ? value.format("HH:mm:ss") : "",
					updated: true
				};
			}

			return updatedSettings;
		});
	}

	const saveCameraSettings = useCallback(() => {
		if (!selectedCameraDetails) {
			notify("Camera details not found", "error");
			return;
		}

		if (cameraSettings.length <= 0) {
			notify("Camera settings not found", "error");
			return;
		}

		const profiles: IUpdateCameraProfileDetails[] = cameraSettings.map((profileItem) => {
			const profileSettings: { [key in CameraControlKeys]?: ICameraControlValues } = {};

			for (const [key, settingValue] of Object.entries(profileItem.settings)) {
				const settingKey = key as CameraControlKeys;
				if (settingValue.type === "IntegerControl") {
					profileSettings[settingKey] = { ...settingValue, value: Number(settingValue.value) };
				} else {
					profileSettings[settingKey] = { ...settingValue };
				}
			}

			return {
				id: profileItem.id,
				name: profileItem.name,
				start_time: profileItem.start_time,
				settings: profileSettings as ICameraControlDetails
			};
		});

		updateCameraProfiles({
			camera_id: selectedCameraDetails.id,
			profiles: profiles
		})
			.unwrap()
			.then(() => {
				notify("Camera settings saved successfully", "success");
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to save camera profiles", "error");
			})
			.finally(() => {
				resetUpdateCameraProfilesState();
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cameraSettings, selectedCameraDetails]);

	function openDeleteProfileConfirmation(profileId: number) {
		setDeleteProfileId(profileId);
	}

	function closeDeleteProfileConfirmation() {
		setDeleteProfileId(-1);
	}

	function handleDeleteCameraProfile() {
		if (!selectedCameraDetails || deleteProfileId < 0) return;

		deleteCameraProfile({ camera_id: selectedCameraDetails.id, profile_id: deleteProfileId })
			.unwrap()
			.then(() => {
				notify("Profile deleted successfully", "success");
				resetDeleteCameraProfileState();
				refetchCameraProfiles();
				closeDeleteProfileConfirmation();
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to delete profile", "error");
			});
	}

	function handleAddNewProfile() {
		setShowAddProfileDialog(true);
	}

	function handleCloseAddProfileDialog(profileDetails?: ICameraControlsData) {
		setShowAddProfileDialog(false);

		if (profileDetails) {
			const profileSettings: { [key in CameraControlKeys]?: ICameraControlValuesInput } = {};

			for (const [key, settingValue] of Object.entries(profileDetails.settings)) {
				const settingKey = key as CameraControlKeys;
				if (settingValue.type === "IntegerControl") {
					profileSettings[settingKey] = { ...settingValue, value: String(settingValue.value) };
				} else {
					profileSettings[settingKey] = { ...settingValue };
				}
			}

			setCameraSettings((currentSettings) => [
				...currentSettings,
				{ ...profileDetails, settings: profileSettings as ICameraControlDetailsInput }
			]);
		}
	}

	function handleOpenCameraSelectionDropdown() {
		setCameraSelectionDropdownOpen(true);
	}

	function handleCloseCameraSelectionDropdown() {
		setCameraSelectionDropdownOpen(false);
	}

	function handleChangeSelectedCamera(cameraDetails: ICameraDetails) {
		setSelectedCameraDetails(cameraDetails);
		handleCloseCameraSelectionDropdown();
	}

	function handleResetProfile(index: number) {
		if (!selectedCameraDetails) {
			notify("Camera details not found", "error");
			return;
		}

		if (!cameraSettings[index]) {
			notify("Camera settings not found", "error");
			return;
		}

		resetCameraProfile({ camera_id: selectedCameraDetails.id, profile_id: cameraSettings[index].id })
			.unwrap()
			.then(() => {
				refetchCameraProfiles().then(() => {
					if (getCameraProfilesResponse)
						setCameraSettings(
							getCameraProfilesResponse.map((profileItem) => {
								const profileSettings: { [key in CameraControlKeys]?: ICameraControlValuesInput } = {};

								for (const [key, settingValue] of Object.entries(profileItem.settings)) {
									const settingKey = key as CameraControlKeys;
									if (settingValue.type === "IntegerControl") {
										profileSettings[settingKey] = { ...settingValue, value: String(settingValue.value) };
									} else {
										profileSettings[settingKey] = { ...settingValue };
									}
								}

								return {
									...profileItem,
									settings: profileSettings as ICameraControlDetailsInput
								};
							})
						);
				});
			})
			.catch((error) => {
				notify("data" in error && error.data ? String(error.data) : "Failed to reset camera profile", "error");
			})
			.finally(() => {
				resetResetCameraProfileState();
			});
	}

	useEffect(() => {
		if (getCameraProfilesResponse) {
			setCameraSettings(
				getCameraProfilesResponse.map((profileItem) => {
					const profileSettings: { [key in CameraControlKeys]?: ICameraControlValuesInput } = {};

					for (const [key, settingValue] of Object.entries(profileItem.settings)) {
						const settingKey = key as CameraControlKeys;
						if (settingValue.type === "IntegerControl") {
							profileSettings[settingKey] = { ...settingValue, value: String(settingValue.value) };
						} else {
							profileSettings[settingKey] = { ...settingValue };
						}
					}

					return {
						...profileItem,
						settings: profileSettings as ICameraControlDetailsInput
					};
				})
			);
		}
	}, [getCameraProfilesResponse]);

	useEffect(() => {
		if (camerasList.length > 0) {
			setSelectedCameraDetails((currentSelectedCamera) => {
				const cameraDetails = camerasList.find((item) => item.id === currentSelectedCamera?.id);
				return cameraDetails ?? camerasList[0] ?? null;
			});
		}
	}, [camerasList]);

	useEffect(() => {
		if (cameraSettings && cameraSettings.some((item) => item.updated)) {
			const timer = setTimeout(saveCameraSettings, 500);
			return () => clearTimeout(timer);
		}
	}, [cameraSettings, saveCameraSettings]);

	// Cleanup debounce timeout on unmount
	useEffect(() => {
		return () => {
			if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
		};
	}, []);

	function renderCameraAdjustmentInput(index: number, key: CameraControlKeys, control: ICameraControlValuesInput) {
		switch (control.type) {
			case "BooleanControl":
				return (
					<Switch
						checked={control.value}
						onChange={(_event, checked) => {
							handleChangeCameraSettingInput(index, key, checked, true);
						}}
						sx={{
							"& .MuiSwitch-switchBase.Mui-checked": {
								color: theme.palette.primary.main
							},
							"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
								backgroundColor: theme.palette.primary.main
							}
						}}
					/>
				);

			case "IntegerControl":
				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
						<Slider
							size="small"
							valueLabelDisplay="auto"
							value={Number(control.value)}
							min={control.min}
							max={control.max}
							step={control.step}
							onChange={(_event, value) => {
								handleChangeCameraSettingInput(index, key, typeof value === "number" ? String(value) : "0", false);
							}}
							onChangeCommitted={(_event, value) => {
								handleChangeCameraSettingInput(index, key, typeof value === "number" ? String(value) : "0", true);
							}}
							sx={{
								flex: 1,
								color: theme.palette.primary.main,
								"& .MuiSlider-thumb": {
									boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)"
								},
								"& .MuiSlider-track": {
									height: 4
								},
								"& .MuiSlider-rail": {
									height: 4
								}
							}}
						/>
						<NumberInput
							size="small"
							type="number"
							value={control.value}
							inputProps={{ step: control.step }}
							minValue={control.min}
							maxValue={control.max}
							onChange={(event) => {
								const value = Number(event.target.value);
								handleChangeCameraSettingInput(index, key, event.target.value, false);
								if (event.target.value && value >= control.min && value <= control.max) {
									handleDebouncedCameraSettingInput(index, key, value);
								}
							}}
							sx={{
								width: "80px",
								"& .MuiOutlinedInput-root": {
									borderRadius: "8px"
								},
								"& .MuiOutlinedInput-input": {
									textAlign: "center",
									padding: "8px 12px"
								}
							}}
						/>
					</Box>
				);

			case "MenuControl":
				return (
					<Select
						fullWidth
						size="small"
						value={control.value}
						onChange={(event) => {
							handleChangeCameraSettingInput(index, key, Number(event.target.value), true);
						}}
						sx={{
							borderRadius: "8px",
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: "rgba(0, 0, 0, 0.12)"
							},
							"&:hover .MuiOutlinedInput-notchedOutline": {
								borderColor: theme.palette.primary.main
							}
						}}
					>
						{Object.entries(control.menu).map(([key, label]) => (
							<MenuItem key={key} value={key}>
								{label}
							</MenuItem>
						))}
					</Select>
				);

			default:
				return null;
		}
	}

	return (
		<Box className="configuration-screen-wrapper flex-wrapper card-layout">
			{/* Header Section */}
			<Box className="configuration-screen-header-wrapper">
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<CameraAltIcon sx={{ color: "primary.main", fontSize: 28 }} />
					<Typography
						variant="h5"
						fontWeight={600}
						color="primary.dark"
						sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" } }}
					>
						Camera Settings
					</Typography>
				</Box>

				{deviceConfig && deviceConfig.device_type !== RPI_DEVICE_NAME ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							flexDirection: { xs: "column", sm: "row" },
							width: { xs: "100%", sm: "auto" }
						}}
					>
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) ? (
							<Button
								variant="contained"
								color="primary"
								startIcon={<AddIcon />}
								onClick={() => openCameraDetailsDialog(true)}
							>
								Add New Camera
							</Button>
						) : null}

						{camerasList.length > 0 ? (
							<>
								<Button
									variant="outlined"
									color="primary"
									ref={cameraSelectionDropdownButtonRef}
									id="camera-selection-dropdown"
									aria-controls="camera-selection-dropdown-menu"
									aria-haspopup
									aria-expanded
									onClick={handleOpenCameraSelectionDropdown}
									endIcon={cameraSelectionDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
								>
									{selectedCameraDetails ? selectedCameraDetails.site_name : "Select Camera"}
								</Button>
								<Menu
									id="camera-selection-dropdown-menu"
									anchorEl={cameraSelectionDropdownButtonRef.current}
									open={cameraSelectionDropdownOpen}
									onClose={handleCloseCameraSelectionDropdown}
									MenuListProps={{ "aria-labelledby": "camera-selection-dropdown" }}
									anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
									transformOrigin={{ horizontal: "right", vertical: "top" }}
									PaperProps={{
										sx: {
											borderRadius: "12px",
											boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
											border: "1px solid rgba(0, 0, 0, 0.06)",
											minWidth: "200px"
										}
									}}
								>
									{camerasList.map((cameraItem) => (
										<MenuItem
											key={cameraItem.id}
											onClick={() => handleChangeSelectedCamera(cameraItem)}
											sx={{
												minWidth: "200px",
												borderRadius: "8px",
												margin: "4px 8px",
												transition: "all 0.2s ease",
												"&:hover": {
													background:
														"linear-gradient(135deg, rgba(91, 78, 191, 0.08) 0%, rgba(91, 78, 191, 0.04) 100%)",
													transform: "translateX(4px)"
												}
											}}
										>
											{cameraItem.site_name}
										</MenuItem>
									))}
								</Menu>
							</>
						) : null}
					</Box>
				) : !RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) && (camerasList.length ?? 0) <= 0 ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							flexDirection: { xs: "column", sm: "row" },
							width: { xs: "100%", sm: "auto" }
						}}
					>
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={() => openCameraDetailsDialog(true)}
						>
							Add New Camera
						</Button>
					</Box>
				) : null}
			</Box>

			{/* Main Loading State */}
			{getCamerasListLoading || getCameraProfilesLoading || getCameraProfilesFetching ? (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						flex: 1,
						gap: 3,
						p: 4
					}}
				>
					<CircularProgress size={48} sx={{ color: "primary.main" }} />
					<Typography variant="h6" color="text.secondary" fontWeight={500}>
						Loading Camera Settings...
					</Typography>
					<Typography variant="body2" color="text.secondary" textAlign="center">
						Please wait while we fetch your camera configuration
					</Typography>
				</Box>
			) : (
				/* Content Section */
				<Box
					className="camera-settings-content-wrapper"
					sx={{
						display: "flex",
						flexDirection: { xs: "column", lg: "row" },
						gap: 3,
						flex: 1,
						minHeight: 0
					}}
				>
					{/* Camera Preview Section */}
					<Box
						sx={{
							flex: { xs: "none", lg: "0.7" },
							minHeight: { xs: "400px", lg: "auto" },
							display: "flex",
							flexDirection: "column",
							height: "100%"
						}}
					>
						{/* Camera Stream */}
						<Box
							sx={{
								flex: 1,
								position: "relative",
								overflow: "hidden"
							}}
						>
							{getCamerasListFetching ? (
								<Box
									sx={{
										width: "100%",
										height: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
										borderRadius: "12px",
										border: "2px dashed rgba(91, 78, 191, 0.3)"
									}}
								>
									<CircularProgress size={32} sx={{ color: "primary.main" }} />
								</Box>
							) : !selectedCameraDetails ? (
								<Box
									sx={{
										width: "100%",
										height: "100%",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
										background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
										borderRadius: "12px",
										border: "2px dashed rgba(91, 78, 191, 0.3)"
									}}
								>
									<NoImageIcon width={64} fill="#d5d5d5" stroke="#d5d5d5" />
									<Typography variant="h6" color="text.secondary" fontWeight={500}>
										No camera selected
									</Typography>
								</Box>
							) : (
								<Box sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
									{selectedCameraDetails.camera_type === "MVP" ? (
										<MVPCameraStream cameraId={selectedCameraDetails.id} />
									) : selectedCameraDetails.camera_type === "UNV" ? (
										<UNVCameraStream cameraId={selectedCameraDetails.id} />
									) : (
										<Stream cameraId={selectedCameraDetails.id} />
									)}
								</Box>
							)}
						</Box>

						{/* Camera Actions */}
						{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.CAMERA_CONFIG) && selectedCameraDetails ? (
							<Box sx={{ py: 2 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
										flexDirection: { xs: "column", sm: "row" }
									}}
								>
									<Button
										variant="contained"
										color="primary"
										startIcon={<VideocamIcon />}
										onClick={() => openCameraDetailsDialog()}
										sx={{
											flex: { xs: "1", sm: "none" },
											width: { xs: "100%", sm: "auto" }
										}}
									>
										Edit Camera
									</Button>

									<Button
										variant="outlined"
										color="error"
										startIcon={<DeleteIcon />}
										onClick={openDeleteConfirmationDialog}
										sx={{
											flex: { xs: "1", sm: "none" },
											width: { xs: "100%", sm: "auto" }
										}}
									>
										Delete Camera
									</Button>
								</Box>
							</Box>
						) : null}
					</Box>

					{/* Camera Settings Section */}
					<Box sx={{ flex: { xs: "none", lg: "0.5" } }}>
						{/* Settings Content */}
						<Box sx={{ height: "100%", overflow: "auto" }}>
							{/* Camera Controls Loading State */}
							{selectedCameraDetails &&
							selectedCameraDetails.rtsp_uri &&
							selectedCameraDetails.rtsp_uri.startsWith("/tmp/") &&
							(getCameraProfilesLoading || getCameraProfilesFetching) ? (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										height: "100%",
										gap: 2,
										p: 4
									}}
								>
									<CircularProgress size={32} sx={{ color: "primary.main" }} />
									<Typography variant="body1" color="text.secondary" fontWeight={500}>
										Loading Camera Controls...
									</Typography>
								</Box>
							) : selectedCameraDetails &&
							  selectedCameraDetails.rtsp_uri &&
							  selectedCameraDetails.rtsp_uri.startsWith("/tmp/") &&
							  cameraSettings ? (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
									{/* Time Settings */}
									<Box
										sx={{
											background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
											borderRadius: "12px",
											p: 3,
											border: "1px solid rgba(0, 0, 0, 0.06)",
											boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
										}}
									>
										<Typography variant="h6" fontWeight={600} color="primary.dark">
											Scene Configuration
										</Typography>
										<Typography variant="subtitle2" fontWeight={600} color="text.secondary">
											{cameraSettings.length} of 5 profiles added
										</Typography>
									</Box>

									{/* Camera Controls */}
									<Box sx={{ flex: 1, overflow: "auto" }}>
										{cameraSettings.map((settingItem, index) => {
											const isActive = activeProfile?.id === settingItem.id;
											return (
												<Box key={`${settingItem.id}-${index}-wrapper`} className="camera-profile-wrapper">
													<AGAccordion
														key={`${settingItem.id}-${index}`}
														expanded={openAccordion === index}
														onChange={() => changeOpenAccordion(index)}
														sx={{
															...(isActive && {
																border: "2px solid var(--color-primary-main)",
																margin: 0
															})
														}}
													>
														<AGAccordionSummary>
															<Box sx={{ flex: 1 }}>
																<Grid container spacing={2} alignItems="center">
																	<Grid item xs={7.5}>
																		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																			<Typography variant="subtitle1" fontWeight={600}>
																				{settingItem.name}
																			</Typography>
																			{isActive && (
																				<Chip
																					label="Active"
																					color="primary"
																					size="small"
																					sx={{
																						fontSize: "0.7rem",
																						height: "20px",
																						fontWeight: 600
																					}}
																				/>
																			)}
																		</Box>
																	</Grid>

																	<Grid item xs={4.5} onClick={(event) => event.stopPropagation()}>
																		<TimePicker
																			closeOnSelect={false}
																			label="Start Time"
																			viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }}
																			slotProps={{
																				textField: {
																					size: "small",
																					fullWidth: true,
																					sx: {
																						"& .MuiOutlinedInput-root": {
																							borderRadius: "8px"
																						}
																					}
																				}
																			}}
																			value={moment(settingItem.start_time, "HH:mm:ss")}
																			onAccept={(value) => handleChangeTime(index, value)}
																		/>
																	</Grid>
																</Grid>
															</Box>
														</AGAccordionSummary>

														<AGAccordionDetails>
															{/* Group Name Input */}
															<Box sx={{ marginBottom: 2 }}>
																<TextField
																	fullWidth
																	size="small"
																	label="Profile Name"
																	value={settingItem.name}
																	onChange={(e) => {
																		handleGroupNameChange(index, e.target.value, false);
																		handleDebouncedGroupNameInput(index, e.target.value);
																	}}
																/>
															</Box>

															<Grid container spacing={2}>
																{Object.entries(settingItem.settings).map(([name, control]) => (
																	<Fragment key={name}>
																		<Grid item xs={12} sm={control.type === "BooleanControl" ? 10 : 12} md={5}>
																			<Box
																				sx={{
																					display: "flex",
																					alignItems: "center",
																					minHeight: 48,
																					p: 1
																				}}
																			>
																				<Typography variant="body2" fontWeight={500} color="text.secondary">
																					{convertSnakeToTitleCase(control.display_name)}
																				</Typography>
																			</Box>
																		</Grid>

																		<Grid item xs={12} sm={control.type === "BooleanControl" ? 2 : 12} md={7}>
																			<Box
																				sx={{
																					display: "flex",
																					alignItems: "center",
																					minHeight: 48,
																					p: 1
																				}}
																			>
																				{renderCameraAdjustmentInput(index, name as CameraControlKeys, control)}
																			</Box>
																		</Grid>
																	</Fragment>
																))}
															</Grid>

															<Box
																sx={{
																	marginTop: 2,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "flex-end",
																	gap: "1rem"
																}}
															>
																<LoadingButton
																	variant="outlined"
																	size="small"
																	color="warning"
																	startIcon={<RotateLeftIcon />}
																	loading={resetCameraProfileLoading}
																	loadingPosition="start"
																	onClick={() => handleResetProfile(index)}
																>
																	Reset Profile
																</LoadingButton>

																{(() => {
																	const isOnlyProfile = cameraSettings.length === 1;
																	const isDisabled = isActive || isOnlyProfile;
																	const tooltipMessage = isOnlyProfile
																		? "Cannot delete the only profile"
																		: isActive
																		? "Cannot delete the active profile"
																		: "";

																	return isDisabled ? (
																		<Tooltip arrow placement="top" title={tooltipMessage}>
																			<span>
																				<Button
																					disabled
																					variant="outlined"
																					size="small"
																					color="error"
																					startIcon={<DeleteIcon />}
																				>
																					Delete Profile
																				</Button>
																			</span>
																		</Tooltip>
																	) : (
																		<Button
																			variant="outlined"
																			size="small"
																			color="error"
																			startIcon={<DeleteIcon />}
																			onClick={() => openDeleteProfileConfirmation(settingItem.id)}
																		>
																			Delete Profile
																		</Button>
																	);
																})()}
															</Box>
														</AGAccordionDetails>
													</AGAccordion>
												</Box>
											);
										})}

										{/* Add New Profile Button */}
										{cameraSettings.length < 5 ? (
											<Grid item xs={12}>
												<Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
													<Button
														variant="outlined"
														color="primary"
														startIcon={<AddCircleIcon />}
														onClick={handleAddNewProfile}
													>
														Add New Profile
													</Button>
												</Box>
											</Grid>
										) : null}
									</Box>
								</Box>
							) : (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										height: "100%",
										gap: 2,
										color: "text.secondary"
									}}
								>
									<SettingsIcon sx={{ fontSize: 48, opacity: 0.5 }} />
									<Typography variant="h6" fontWeight={500}>
										No Settings Available
									</Typography>
									<Typography variant="body2" textAlign="center">
										Camera settings are only available for local cameras
									</Typography>
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			)}

			{/* Loading Overlay */}
			{updateCameraProfilesLoading ? (
				<Box
					sx={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(255, 255, 255, 0.8)",
						zIndex: 9999
					}}
				>
					<CircularProgress size={48} sx={{ color: "primary.main" }} />
				</Box>
			) : null}

			<CameraSetupDialog
				open={showCameraDetailsDialog}
				cameraDetails={!addingNewCamera ? selectedCameraDetails : null}
				onClose={closeCameraDetailsDialog}
			/>

			<ConfirmationDialog
				open={showDeleteConfirmationDialog}
				title="Delete Camera"
				heading={"Are you sure you want to remove this camera from the system?"}
				onClose={closeDeleteConfirmationDialog}
				onConfirm={handleDeleteCamera}
				color="error"
				confirmButtonLoading={deleteCameraLoading}
				width="sm"
				confirmationButtonText="Delete"
			/>

			<ConfirmationDialog
				open={deleteProfileId >= 0}
				title="Delete Profile"
				heading="Are you sure you want to delete this profile?"
				onClose={closeDeleteProfileConfirmation}
				onConfirm={handleDeleteCameraProfile}
				color="error"
				confirmButtonLoading={deleteCameraProfileLoading}
				width="xs"
				confirmationButtonText="Delete"
			/>

			<AddProfileDialog
				open={showAddProfileDialog}
				cameraId={selectedCameraDetails?.id ?? 0}
				onClose={handleCloseAddProfileDialog}
			/>
		</Box>
	);
};

export default CameraSettings;
